export type ExtractedEmailImages = {
  text: string;
  para1Image?: string;
  para2Image?: string;
};

const PARA1_TAG = '{{ paragraph1Image }}';
const PARA2_TAG = '{{ paragraph2Image }}';
const PARA1_FILE = 'paragraph1Image.png';
const PARA2_FILE = 'paragraph2Image.png';
const IMG_TAG_RE = /<img\b[^>]*>/gi;

const decodeImgSrc = (src: string) => src.replace(/&amp;/g, '&');

const stripQuery = (src: string) => decodeImgSrc(src).split('?')[0] ?? '';

const readImgSrc = (tag: string): string => {
  const quoted = /src\s*=\s*(["'])([\s\S]*?)\1/i.exec(tag);
  if (quoted?.[2]) return quoted[2];
  const bare = /src\s*=\s*([^\s>]+)/i.exec(tag);
  return bare?.[1] ?? '';
};

const replaceImgSrc = (tag: string, mergeTag: string): string => {
  if (/src\s*=\s*"/i.test(tag)) {
    return tag.replace(/src\s*=\s*"[^"]*"/i, `src="${mergeTag}"`);
  }
  if (/src\s*=\s*'/i.test(tag)) {
    return tag.replace(/src\s*=\s*'[^']*'/i, `src="${mergeTag}"`);
  }
  if (/src\s*=\s*[^\s>]+/i.test(tag)) {
    return tag.replace(/src\s*=\s*[^\s>]+/i, `src="${mergeTag}"`);
  }
  return tag.replace(/<img/i, `<img src="${mergeTag}"`);
};

const pathHasFile = (src: string, fileName: string): boolean => {
  if (!fileName) return false;
  const path = stripQuery(src);
  const encoded = encodeURIComponent(fileName);
  return (
    path.endsWith(`/${fileName}`) ||
    path.endsWith(`/${encoded}`) ||
    path.includes(`/template/${fileName}`) ||
    path.includes(`/template/${encoded}`)
  );
};

const sameStoredUrl = (src: string, initialUrl?: string): boolean => {
  if (!src || !initialUrl) return false;
  return (
    decodeImgSrc(src) === decodeImgSrc(initialUrl) ||
    stripQuery(src) === stripQuery(initialUrl)
  );
};

const isNewUploadSrc = (src: string): boolean =>
  src.startsWith('data:image/') || src.startsWith('blob:');

const isGcsSrc = (src: string): boolean =>
  decodeImgSrc(src).includes('storage.googleapis.com');

/** TipTap leftover duplicate sits against the remapped img. A real second
 * image is in another block (`</p><p>`, etc.), not only whitespace. */
const isImmediatelyAdjacent = (
  html: string,
  leftEnd: number,
  rightStart: number,
): boolean =>
  leftEnd <= rightStart && /^[\s]*$/.test(html.slice(leftEnd, rightStart));

const dedupeMergeImages = (html: string): string => {
  let seenPara1 = false;
  let seenPara2 = false;
  return html.replace(IMG_TAG_RE, (tag) => {
    if (tag.includes(PARA1_TAG)) {
      if (seenPara1) return '';
      seenPara1 = true;
      return tag;
    }
    if (tag.includes(PARA2_TAG)) {
      if (seenPara2) return '';
      seenPara2 = true;
      return tag;
    }
    return tag;
  });
};

const stripBlobImages = (html: string): string =>
  html.replace(IMG_TAG_RE, (tag) =>
    decodeImgSrc(readImgSrc(tag)).startsWith('blob:') ? '' : tag,
  );

/**
 * Turns editor/wizard HTML <img> tags back into merge tags.
 *
 * Lester ENR-3077 3rd FAIL: generic "next free slot" mapping turned extra
 * copies of the same GCS image into {{ paragraph2Image }}, left blob: URLs
 * in the PATCH, and mixed signed URLs with merge tags.
 */
export function extractBothImagesFromContent(
  htmlContent: string,
  initialPara1ImageUrl?: string,
  initialPara2ImageUrl?: string,
  occupied: { para1?: boolean; para2?: boolean } = {},
): ExtractedEmailImages {
  if (typeof htmlContent !== 'string') return { text: '' };

  let para1Image: string | undefined;
  let para2Image: string | undefined;
  let para1Handled = Boolean(occupied.para1);
  let para2Handled = Boolean(occupied.para2);
  let para1FromStored = Boolean(occupied.para1);
  let para2FromStored = Boolean(occupied.para2);
  const hasStoredPara2 = Boolean(initialPara2ImageUrl);
  const gcsSlotByPath = new Map<string, 'para1' | 'para2'>();

  const matchesPara1 = (src: string) =>
    pathHasFile(src, PARA1_FILE) || sameStoredUrl(src, initialPara1ImageUrl);
  const matchesPara2 = (src: string) =>
    pathHasFile(src, PARA2_FILE) || sameStoredUrl(src, initialPara2ImageUrl);

  const assignNew = (src: string): 'para1' | 'para2' | null => {
    if (!para1Handled) {
      para1Image = src;
      para1Handled = true;
      return 'para1';
    }
    if (!para2Handled) {
      para2Image = src;
      para2Handled = true;
      return 'para2';
    }
    return null;
  };

  const assignUnknownGcs = (src: string): 'para1' | 'para2' | null => {
    if (!para1Handled) {
      para1Handled = true;
      para1FromStored = true;
      return 'para1';
    }
    if (!para2Handled) {
      para2Handled = true;
      para2FromStored = true;
      return 'para2';
    }
    return null;
  };

  const storedPara1Ranges: Array<{ start: number; end: number }> = [];
  const imgMatches = Array.from(htmlContent.matchAll(IMG_TAG_RE));

  const rewriteTag = (
    fullImgTag: string,
    tagStart: number,
    tagEnd: number,
  ): string => {
    const imgSrc = readImgSrc(fullImgTag);
    const decodedSrc = imgSrc ? decodeImgSrc(imgSrc) : imgSrc;

    if (!decodedSrc) return fullImgTag;

    if (decodedSrc.includes(PARA1_TAG)) {
      para1Handled = true;
      para1FromStored = true;
      storedPara1Ranges.push({ start: tagStart, end: tagEnd });
      return fullImgTag;
    }
    if (decodedSrc.includes(PARA2_TAG)) {
      para2Handled = true;
      para2FromStored = true;
      return fullImgTag;
    }

    if (matchesPara1(decodedSrc)) {
      para1Handled = true;
      para1FromStored = true;
      gcsSlotByPath.set(stripQuery(decodedSrc), 'para1');
      storedPara1Ranges.push({ start: tagStart, end: tagEnd });
      return replaceImgSrc(fullImgTag, PARA1_TAG);
    }
    if (matchesPara2(decodedSrc)) {
      para2Handled = true;
      para2FromStored = true;
      gcsSlotByPath.set(stripQuery(decodedSrc), 'para2');
      return replaceImgSrc(fullImgTag, PARA2_TAG);
    }

    if (isNewUploadSrc(decodedSrc)) {
      // Drop a TipTap leftover blob glued to a remapped GCS image.
      // A real second image lives in another block, so keep that upload
      // even when paragraph2 was empty (Salman re-review).
      const leftoverBesideStoredPara1 =
        para1FromStored &&
        !para2FromStored &&
        !hasStoredPara2 &&
        para1Handled &&
        storedPara1Ranges.some(
          (range) =>
            isImmediatelyAdjacent(htmlContent, range.end, tagStart) ||
            isImmediatelyAdjacent(htmlContent, tagEnd, range.start),
        );
      if (leftoverBesideStoredPara1) {
        return '';
      }
      const slot = assignNew(decodedSrc);
      if (slot === 'para1') return replaceImgSrc(fullImgTag, PARA1_TAG);
      if (slot === 'para2') return replaceImgSrc(fullImgTag, PARA2_TAG);
      return '';
    }

    if (isGcsSrc(decodedSrc)) {
      const pathKey = stripQuery(decodedSrc);
      const reused = gcsSlotByPath.get(pathKey);
      if (reused === 'para1') {
        storedPara1Ranges.push({ start: tagStart, end: tagEnd });
        return replaceImgSrc(fullImgTag, PARA1_TAG);
      }
      if (reused === 'para2') return replaceImgSrc(fullImgTag, PARA2_TAG);
      const slot = assignUnknownGcs(decodedSrc);
      if (slot) {
        gcsSlotByPath.set(pathKey, slot);
        if (slot === 'para1') {
          storedPara1Ranges.push({ start: tagStart, end: tagEnd });
        }
        return replaceImgSrc(
          fullImgTag,
          slot === 'para1' ? PARA1_TAG : PARA2_TAG,
        );
      }
      return '';
    }

    return fullImgTag;
  };

  let processedHtml = '';
  let cursor = 0;
  for (const match of imgMatches) {
    const fullImgTag = match[0];
    const tagStart = match.index ?? 0;
    const tagEnd = tagStart + fullImgTag.length;
    processedHtml += htmlContent.slice(cursor, tagStart);
    processedHtml += rewriteTag(fullImgTag, tagStart, tagEnd);
    cursor = tagEnd;
  }
  processedHtml += htmlContent.slice(cursor);

  processedHtml = dedupeMergeImages(processedHtml);
  processedHtml = stripBlobImages(processedHtml);

  return {
    text: processedHtml,
    para1Image,
    para2Image,
  };
}
